import { useMemo } from "react"
import { get, isNumber } from "radash"

import { PaymentOptionKey } from "@be-miniapp/types"

interface UsePaymentMethodParams<TOption> {
  paymentMethodCode: number | undefined
  paymentOptions: unknown
  nestedDataPath?: string
  getMethodCode: (option: TOption) => number | undefined
}

function toPaymentOptionList<TOption>(value: unknown): TOption[] | undefined {
  if (Array.isArray(value)) return value as TOption[]
  return undefined
}

function getDataPaymentOptionList<TOption>(
  value: unknown
): TOption[] | undefined {
  if (!value || typeof value !== "object") return undefined
  return toPaymentOptionList<TOption>((value as { data?: unknown }).data)
}

function normalizePaymentOptions<TOption>({
  paymentOptions,
  nestedDataPath,
}: {
  paymentOptions: unknown
  nestedDataPath?: string
}): TOption[] {
  if (!nestedDataPath) return toPaymentOptionList<TOption>(paymentOptions) ?? []

  const directValue = get(paymentOptions, nestedDataPath)
  const directList =
    toPaymentOptionList<TOption>(directValue) ??
    getDataPaymentOptionList<TOption>(directValue)
  if (directList) return directList

  const wrappedValue = get({ data: paymentOptions }, nestedDataPath)
  return (
    toPaymentOptionList<TOption>(wrappedValue) ??
    getDataPaymentOptionList<TOption>(wrappedValue) ??
    getDataPaymentOptionList<TOption>(paymentOptions) ??
    []
  )
}

function usePaymentMethod<TOption>({
  paymentMethodCode = PaymentOptionKey.CASH,
  paymentOptions,
  nestedDataPath,
  getMethodCode,
}: UsePaymentMethodParams<TOption>): TOption | undefined {
  return useMemo(() => {
    const listPaymentOptions = normalizePaymentOptions<TOption>({
      paymentOptions,
      nestedDataPath,
    })

    const matched = listPaymentOptions.find(
      (option) => getMethodCode(option) === paymentMethodCode
    )
    if (matched) return matched

    return listPaymentOptions.find((option) => isNumber(getMethodCode(option)))
  }, [getMethodCode, nestedDataPath, paymentMethodCode, paymentOptions])
}

export { usePaymentMethod }
export type { UsePaymentMethodParams }
